import React, { useEffect, useMemo, useState, ChangeEvent } from 'react';
import './style.scss';
import moment from 'moment-timezone';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import {
  AdministeredDrugValue,
  MedicalOrder,
  Patient,
  PhysicianPhysicianTypeEnum,
} from 'generated/api';
import { api } from 'careand-redux/api/ApiThunks';
import DefaultModal from 'components/Utils/DefaultModal';
import PatientBadge from 'components/Patient/PatientBadge';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import { MedicalOrderOptions } from 'components/HealthRecords/Prescriptions/NewPrescription/constants';
import { TextareaAutosize } from '@material-ui/core';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { StaffBadge } from 'services/StaffUtils';
import { GetStaff, IsTypeNurse } from 'careand-redux/selectors';
import { PhysicianCheckbox } from 'components/HealthRecords/Appointments/NewAppointment/SchedulePhysician';
import StackItemsView from 'components/Common/StackItemsView';
import MenuItem from '@material-ui/core/MenuItem';
import Icon from 'components/Utils/NewIcon';
import CPPUtils from 'services/CPPUtils';
import { setFormValues } from 'hooks/useReduxForm';

const ACTIVE_TAB = 'administered_drugs';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  setActiveCPPTab: (tab: string) => void;
  patient: Patient;
  editable?: boolean;
  order?: MedicalOrder;
};

const MedicalOrderModal = ({
  isOpen,
  onClose,
  patient,
  editable,
  order,
  setActiveCPPTab,
}: Props) => {
  const dispatch = useAppDispatch();
  const saving = useIsLoading(api.HealthRecords.addOrEditMedicalOrder);
  const deleting = useIsLoading(api.HealthRecords.deleteMedicalOrder);
  const practitioners = useAppSelector((state) => state.physicians.byId);
  const admins = useAppSelector((state) => state.admins.byId);

  const author = order?.author_id && (practitioners[order!.author_id] || admins[order!.author_id]);
  const user = useAppSelector(GetStaff);
  const activeUserId = user?.id;
  const isAssignedUser = activeUserId === order?.user_id;

  const [medications, setMedications] = useState<AdministeredDrugValue[]>(order?.medications || []);

  const nurses = useMemo(() => {
    let rpns = Object.values(practitioners).filter(
      (p) =>
        !!p &&
        ((p.physician_type === PhysicianPhysicianTypeEnum.Nurse && !p.inactive) ||
          p.id === order?.user_id),
    );

    const own = rpns.filter((p) => p?.clinic_id === (author || user)?.clinic_id);
    const otherClinics = rpns.filter((p) => !(p?.clinic_id === (author || user)?.clinic_id));

    return [...own, ...otherClinics];
  }, [practitioners, author, user, order?.user_id]);

  const [text, setText] = useState<string>(order?.text || '');
  const [targetUserId, setTargetUserId] = useState<string | undefined>(
    order?.user_id || nurses[0]?.id,
  );

  const isNurse = useAppSelector(IsTypeNurse);

  useEffect(() => {
    if (isOpen && !editable && order && isNurse && user?.id && !order.read_by?.includes(user.id!)) {
      dispatch(
        api.HealthRecords.addOrEditMedicalOrder({
          patientId: patient.id!,
          medicalOrder: {
            ...order,
            read_by: [...(order.read_by || []), user.id],
          },
        }),
      );
    }
  }, [dispatch, isOpen, order, user, editable, isNurse, patient]);

  async function onSave() {
    const medicalOrder = {
      date: moment().unix(),
      author_id: activeUserId!,
      user_id: targetUserId,
      medications,
      text,
    };
    const res = await dispatch(
      api.HealthRecords.addOrEditMedicalOrder({
        patientId: patient.id!,
        medicalOrder,
      }),
      {
        successTitle: 'Medical Order Added',
        errorTitle: 'Error adding medical order',
      },
    );
    if (res) {
      onClose();
    }
  }

  async function onDelete() {
    const res = await dispatch(
      api.HealthRecords.deleteMedicalOrder({
        patientId: patient.id!,
        date: order!.date,
      }),
      {
        successTitle: 'Order deleted successfully.',
        errorTitle: 'Error deleting order',
      },
    );

    if (res) {
      onClose();
    }
  }

  function describeMedication(value: AdministeredDrugValue) {
    return `${value.name} ${value.dosage} ${value.route} ${value.notes}`;
  }

  function addMedication(medication: AdministeredDrugValue) {
    setMedications((prevState) => [...prevState, medication]);
  }

  function removeMedication(medication: AdministeredDrugValue) {
    setMedications((prevState) =>
      prevState.filter((m) => describeMedication(m) !== describeMedication(medication)),
    );
  }

  function onAddToCPP(medication: AdministeredDrugValue) {
    setActiveCPPTab(ACTIVE_TAB);
    const formName = CPPUtils.getFormName(ACTIVE_TAB, patient.id!);
    dispatch(setFormValues({ formName, values: { ...medication, given_date: moment().unix() } }));
    onClose();
  }

  return (
    isOpen && (
      <DefaultModal
        className="height-auto"
        onRequestClose={onClose}
        content={
          <div className="medical-order-modal-content">
            <section className="patient">
              <span className="small-title">Patient</span>
              <PatientBadge patient={patient} />
            </section>
            {author && !editable && (
              <>
                <section className="author">
                  <span className="small-title">Author</span>
                  <StaffBadge user={author} />
                </section>
              </>
            )}
            {editable && (
              <section className="options">
                <span className="small-title">Medications</span>
                <DefaultSelectField
                  key={medications.length}
                  placeholder="option"
                  name="options"
                  width="100%"
                  inputElements={MedicalOrderOptions.map((option, index) => {
                    return (
                      //@ts-expect-error
                      <MenuItem value={option} key={'select-concern-' + option.name + index}>
                        {describeMedication(option)}
                      </MenuItem>
                    );
                  })}
                  inputs={MedicalOrderOptions}
                  onChange={(ev: ChangeEvent<HTMLInputElement>) => {
                    addMedication(ev.target.value as unknown as AdministeredDrugValue);
                  }}
                />
              </section>
            )}
            <StackItemsView
              stack={medications}
              onDeleteItem={editable ? removeMedication : undefined}
              describeItem={(medication) => describeMedication(medication)}
              additionalButton={
                isAssignedUser
                  ? (item) => <Icon src="plus-circle" onClick={() => onAddToCPP(item)} />
                  : undefined
              }
            />
            <section className="text">
              <span className="small-title">Order</span>
              <TextareaAutosize
                value={text}
                onChange={(ev) => setText(ev.target.value)}
                // readOnly rather than disabled: a disabled textarea takes no mouse
                // events, so nurses could not scroll through a long order.
                readOnly={!editable}
              />
            </section>
            {nurses.length > 0 && (
              <section className="target-user">
                <span className="small-title">Selected RPN</span>
                <div className="target-user-list">
                  {nurses.map((nurse) => {
                    const selected = nurse?.id === targetUserId;
                    return (
                      nurse && (
                        <PhysicianCheckbox
                          key={nurse.id}
                          physician={nurse}
                          active={true}
                          selected={selected}
                          onClick={
                            editable
                              ? () => setTargetUserId(selected ? undefined : nurse.id)
                              : undefined
                          }
                        />
                      )
                    );
                  })}
                </div>
              </section>
            )}
            <section className="buttons">
              {editable && (
                <>
                  <DefaultButton name="Cancel" onClick={onClose} disabled={saving || deleting} />
                  <DefaultButton name="Save" className="blue" onClick={onSave} loading={saving} />
                </>
              )}
              {!editable && (
                <DefaultButton
                  name="Delete"
                  className="red"
                  onClick={onDelete}
                  loading={deleting}
                />
              )}
            </section>
          </div>
        }
        isOpen={isOpen}
        wrap
        title="Medical Order"
      />
    )
  );
};

export default MedicalOrderModal;
